/* Team Work map — Mapbox public map centered on Kinshasa; listing zones are geocoded approximately, never treated as exact addresses. */
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";
const KINSHASA_CENTER = [15.2663, -4.4419];
const geocodeCache = new Map();

function popupContent(listing) {
  const root = document.createElement("div");
  root.className = "tw-map-popup";
  const title = document.createElement("strong");
  title.textContent = listing.title || "Offre Team Work";
  const place = document.createElement("span");
  place.textContent = [listing.zone, listing.city].filter(Boolean).join(" · ") || "Kinshasa, RDC";
  root.append(title, place);
  return root;
}

async function geocodeListing(listing, token) {
  const query = [listing.zone, listing.city, listing.country || "RDC"].filter(Boolean).join(", ");
  if (!query) return null;
  if (geocodeCache.has(query)) return geocodeCache.get(query);
  try {
    const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${encodeURIComponent(token)}&language=fr&country=cd&limit=1`);
    if (!response.ok) return null;
    const data = await response.json();
    const center = data.features?.[0]?.center;
    const result = Array.isArray(center) && center.length === 2 ? center : null;
    geocodeCache.set(query, result);
    return result;
  } catch {
    return null;
  }
}

export default function MapboxListingsMap({ listings = [] }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const loadedRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState("");
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    if (!MAPBOX_TOKEN || !containerRef.current || mapRef.current) return undefined;
    mapboxgl.accessToken = MAPBOX_TOKEN;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/light-v11",
      center: KINSHASA_CENTER,
      zoom: 10.8,
      attributionControl: true,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    const onLoad = () => {
      loadedRef.current = true;
      setMapReady(true);
      window.requestAnimationFrame(() => map.resize());
    };
    const onStyleLoad = () => map.resize();
    const onError = () => {
      if (!loadedRef.current) setMapError("La carte n’a pas pu être chargée. Vérifie le jeton Mapbox, le domaine autorisé et l’accès WebGL du navigateur.");
    };
    map.on("load", onLoad);
    map.on("style.load", onStyleLoad);
    map.on("error", onError);
    const timeoutId = window.setTimeout(() => {
      if (!loadedRef.current) setMapError("Le fond cartographique met trop de temps à répondre. Actualise la page ou vérifie la configuration Mapbox dans Netlify.");
    }, 12000);
    mapRef.current = map;
    return () => {
      window.clearTimeout(timeoutId);
      map.off("load", onLoad);
      map.off("style.load", onStyleLoad);
      map.off("error", onError);
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      loadedRef.current = false;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !MAPBOX_TOKEN) return undefined;
    let cancelled = false;
    const renderMarkers = async () => {
      setGeocoding(true);
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      const located = [];
      for (const listing of listings.slice(0, 100)) {
        const center = await geocodeListing(listing, MAPBOX_TOKEN);
        if (cancelled || !center) continue;
        located.push({ listing, center });
      }
      if (cancelled) return;
      const bounds = new mapboxgl.LngLatBounds();
      located.forEach(({ listing, center }) => {
        const marker = new mapboxgl.Marker({ color: "#FE6C09" })
          .setLngLat(center)
          .setPopup(new mapboxgl.Popup({ offset: 24 }).setDOMContent(popupContent(listing)))
          .addTo(mapRef.current);
        markersRef.current.push(marker);
        bounds.extend(center);
      });
      if (located.length > 1) mapRef.current.fitBounds(bounds, { padding: 70, maxZoom: 12.5, duration: 500 });
      setGeocoding(false);
    };
    renderMarkers();
    return () => { cancelled = true; };
  }, [listings, mapReady]);

  if (!MAPBOX_TOKEN) {
    return <div className="flex h-[420px] items-center justify-center rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-900" data-testid="map-view-error">La carte est prête mais le jeton Mapbox n’est pas configuré dans Netlify.</div>;
  }

  return (
    <div className="mapbox-map-shell relative h-[420px] overflow-hidden rounded-2xl border border-slate-200 bg-brand-mist" data-testid="map-view">
      <div ref={containerRef} className="absolute inset-0" aria-label="Carte des offres Team Work à Kinshasa" />
      {geocoding && <div className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-2 text-xs font-bold text-brand-navy shadow-lg">Localisation des offres…</div>}
      {mapError && <div className="absolute inset-x-4 bottom-4 rounded-xl bg-white/95 px-4 py-3 text-sm font-semibold text-red-700 shadow-lg">{mapError}</div>}
    </div>
  );
}
